import { Button } from '@/_components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/_components/ui/command'
import { FormControl } from '@/_components/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/_components/ui/popover'
import { cn } from '@/_lib/utils'
import type { TUpdatePersonForm } from '@/_schemas/update-person'
import { queries } from '@/features/_lib/react-query'
import type { TExposedFacilityDTO } from '@doutorsim/shared/facilities'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useState } from 'react'
import type { UseFormResetField } from 'react-hook-form'

interface FacilitySelectProps {
  selectedFacility?: TExposedFacilityDTO | null
  onChange: (...event: any[]) => void
  resetField?: UseFormResetField<TUpdatePersonForm>
  isLoading: boolean
  popoverWidth?: number
  disabled?: boolean
}

export function FacilitySelect({
  selectedFacility,
  onChange,
  resetField,
  isLoading,
  popoverWidth = 200,
  disabled,
}: FacilitySelectProps) {
  const [open, setOpen] = useState(false)
  const { data: availableFacilities = [], isPending } = useQuery(queries.staffFacilities.list)

  const handleSelectClick = () => {
    // Data is fetched automatically by the hook
  }

  const handleSelect = (facility: TExposedFacilityDTO) => {
    const isAlreadySelected = facility.id === selectedFacility?.id

    if (isAlreadySelected) {
      if (resetField) {
        resetField('facility' as any, { defaultValue: undefined })
      }
      return setOpen(false)
    }

    onChange(facility)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <FormControl>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={`w-full justify-between hover:bg-transparent hover:text-brand-grayscale-12 disabled:pointer-events-none disabled:bg-brand-grayscale-5`}
            onClick={handleSelectClick}
            disabled={isLoading || disabled}
          >
            {selectedFacility ? (
              <>
                {selectedFacility.name}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </>
            ) : (
              <>
                Selecione a instituição
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </>
            )}
          </Button>
        </FormControl>
      </PopoverTrigger>
      {!isPending && (
        <PopoverContent className="p-0" style={{ width: popoverWidth }}>
          <Command>
            <CommandInput placeholder="Selecione a instituição" />
            <CommandEmpty>Nenhuma instituição encontrada.</CommandEmpty>
            <CommandGroup className="scrollbar max-h-60 overflow-y-auto">
              {availableFacilities?.map((facility) => (
                <CommandItem
                  key={facility.id}
                  value={facility.name}
                  onSelect={() => handleSelect(facility)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      selectedFacility?.id === facility.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {facility.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      )}
    </Popover>
  )
}
